import { Item } from '@/components/blocks/UsePassDialog';
import { getEffectivePrice } from './pricing';
import { createClient } from './supabase/client';

export interface TwoForOnePromotion {
    id: string;
    festival_id: string;
    name: string;
    promotion_type: 'GLOBAL' | 'SELECTED_SCREENING';
    is_active: boolean;
    start_date?: string | null;
    end_date?: string | null;
    screening_type_filter?: 'ONLINE' | 'OFFLINE' | null;
    screening_ids?: string[] | null;
}

export interface TwoForOneDiscountResult {
    discountAmount: number;
    freeTickets: number;
    promotionId?: string;
    promotionName?: string;
    itemDiscounts: Map<number, number>; // item index -> discount amount
    freeItemIndices: Set<number>; // indices of items with free tickets
}

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://image-nation.org/api-service';

/**
 * Fetch active promotions for a festival
 * Tries API endpoint first, falls back to Supabase if API is not available
 */
export async function getActivePromotions(festivalId: string): Promise<TwoForOnePromotion[]> {
    // Try API endpoint first
    try {
        const url = `${API_URL}/two-for-one-promotions?festivalId=${festivalId}`;
        
        const response = await fetch(url);
        
        if (response.ok) {
            const data = await response.json();
            
            // Handle both response formats: { promotions: [...] } or direct array
            const promotions = data.promotions || data || [];
            
            if (Array.isArray(promotions) && promotions.length > 0) {
                return promotions;
            }
        }
    } catch (error) {
        // API fetch failed, try Supabase fallback
    }

    // Fallback to Supabase direct query
    try {
        const supabase = createClient();
        
        // Use type assertion since table may not be in types yet
        // Query: festival_id matches, is_active = true, and dates are valid
        const { data, error } = await (supabase
            .from('two_for_one_promotions' as any)
            .select('*')
            .eq('festival_id', festivalId)
            .eq('is_active', true) as any);

        if (error) {
            // If table doesn't exist, that's expected - API team needs to create it
            return [];
        }
        
        // Filter by date ranges (Supabase query above handles nulls, but we double-check)
        const activePromotions = ((data || []) as any[]).filter((promo: any) => {
            if (!promo.is_active) {
                return false;
            }
            
            const nowDate = new Date();
            
            if (promo.start_date) {
                const startDate = new Date(promo.start_date);
                if (startDate > nowDate) {
                    return false;
                }
            }
            
            if (promo.end_date) {
                const endDate = new Date(promo.end_date);
                // Promotion is active if current time is before or equal to end_date
                // Note: end_date at 15:00:00 means promotion is active until 15:00:00 (inclusive)
                if (nowDate > endDate) {
                    return false;
                }
            }
            
            // Normalize screening_type_filter: null or 'ALL' means all screenings
            if (promo.screening_type_filter === 'ALL' || promo.screening_type_filter === null) {
                promo.screening_type_filter = null;
            }
            
            return true;
        });
        
        return activePromotions as TwoForOnePromotion[];
    } catch (error) {
        return [];
    }
}

/**
 * Check if a promotion is currently active based on date ranges
 */
export function isPromotionActive(promotion: TwoForOnePromotion): boolean {
    if (!promotion.is_active) {
        return false;
    }

    const now = new Date();

    if (promotion.start_date) {
        const start = new Date(promotion.start_date);
        if (now < start) {
            return false;
        }
    }

    if (promotion.end_date) {
        const end = new Date(promotion.end_date);
        // Promotion is active if current time is before or equal to end_date
        // Note: end_date at 15:00:00 means promotion is active until 15:00:00 (inclusive)
        if (now > end) {
            return false;
        }
    }

    return true;
}

/**
 * Calculate free ticket count based on quantity (multiples logic)
 * 2 tickets = 1 free, 4 tickets = 2 free, etc.
 */
export function getFreeTicketCount(quantity: number): number {
    return Math.floor(quantity / 2);
}

/**
 * Check if an item is eligible for a promotion
 */
export function isEligibleForPromotion(
    item: Item,
    promotion: TwoForOnePromotion,
    itemIndex: number
): boolean {
    // Defensive check: item must have a ticket
    if (!item.ticket) {
        return false;
    }

    // Defensive check: item must have quantity > 0
    if (!item.quantity || item.quantity <= 0) {
        return false;
    }

    // Exclude items that are fully covered by passes
    // But allow items that have some uncovered tickets (pass can cover part of quantity)
    const passQuantityUsed = item.passQuantityUsed || 0;
    const uncoveredQuantity = item.quantity - passQuantityUsed;
    
    // If all tickets are covered by pass, exclude from promotion
    if (uncoveredQuantity <= 0) {
        return false;
    }
    
    // Note: We don't exclude items with usedPassId if they have uncovered tickets
    // This allows 2-for-1 to apply to uncovered tickets even if some are covered by pass

    // Only apply to single-use tickets (full-price tickets)
    // A single-use ticket is one where single_use is true
    // OR where online === 1 AND in_cinema === 1
    const isSingleUseTicket = 
        item.ticket.usage_limits.single_use === true ||
        (item.ticket.usage_limits.online === 1 && item.ticket.usage_limits.in_cinema === 1);
    
    if (!isSingleUseTicket) {
        return false;
    }

    // For GLOBAL promotions, check screening type filter
    if (promotion.promotion_type === 'GLOBAL') {
        // Handle screening_type_filter: null, undefined, or 'ALL' means "ALL", otherwise check match
        const filter = promotion.screening_type_filter as string | null | undefined;
        
        // If filter is null, undefined, or 'ALL', allow all screening types
        if (!filter || filter === 'ALL' || filter === 'null') {
            return true;
        }
        
        // Only filter if filter is specifically 'ONLINE' or 'OFFLINE'
        if (filter === 'ONLINE' || filter === 'OFFLINE') {
            if (item.screening?.screening_type !== filter) {
                return false;
            }
        }
        
        return true;
    }

    // For SELECTED_SCREENING promotions, check if screening is in the list
    if (promotion.promotion_type === 'SELECTED_SCREENING') {
        if (!item.screening?.id) {
            return false;
        }
        return promotion.screening_ids?.includes(item.screening.id) ?? false;
    }

    return false;
}

/**
 * Calculate 2-for-1 discount preview for UI display
 * Note: This is for preview only - API will perform final validation
 */
export function calculateTwoForOneDiscountPreview(
    items: Item[],
    promotions: TwoForOnePromotion[],
    festivalId: string
): TwoForOneDiscountResult {
    const result: TwoForOneDiscountResult = {
        discountAmount: 0,
        freeTickets: 0,
        itemDiscounts: new Map(),
        freeItemIndices: new Set(),
    };

    // Filter active promotions for this festival
    const activePromotions = promotions.filter(
        (p) => p.festival_id === festivalId && isPromotionActive(p)
    );

    if (activePromotions.length === 0) {
        return result;
    }

    // Process each promotion
    // Option 1: Only apply the best promotion (current behavior)
    // Option 2: Apply all promotions that don't conflict (commented out below)
    
    // CURRENT: Apply only the promotion with the highest discount
    for (const promotion of activePromotions) {
        let promotionResult: TwoForOneDiscountResult;

        if (promotion.promotion_type === 'GLOBAL') {
            promotionResult = calculateGlobalPromotionDiscount(items, promotion);
        } else {
            promotionResult = calculateSelectedScreeningDiscount(items, promotion);
        }

        // Use the promotion with the highest discount
        if (promotionResult.discountAmount > result.discountAmount) {
            result.discountAmount = promotionResult.discountAmount;
            result.freeTickets = promotionResult.freeTickets;
            result.promotionId = promotion.id;
            result.promotionName = promotion.name;
            result.itemDiscounts = promotionResult.itemDiscounts;
            result.freeItemIndices = promotionResult.freeItemIndices;
        }
    }

    // ALTERNATIVE: Apply all promotions (uncomment to enable)
    // This would allow multiple promotions to apply simultaneously
    // for (const promotion of activePromotions) {
    //     let promotionResult: TwoForOneDiscountResult;
    //     const alreadyFreeIndices = new Set(result.freeItemIndices);

    //     if (promotion.promotion_type === 'GLOBAL') {
    //         promotionResult = calculateGlobalPromotionDiscount(items, promotion, alreadyFreeIndices);
    //     } else {
    //         promotionResult = calculateSelectedScreeningDiscount(items, promotion, alreadyFreeIndices);
    //     }

    //     // Combine results (avoid double-discounting the same item)
    //     result.discountAmount += promotionResult.discountAmount;
    //     result.freeTickets += promotionResult.freeTickets;
    //     promotionResult.itemDiscounts.forEach((discount, index) => {
    //         const existing = result.itemDiscounts.get(index) || 0;
    //         result.itemDiscounts.set(index, existing + discount);
    //     });
    //     promotionResult.freeItemIndices.forEach((index) => result.freeItemIndices.add(index));
    // }

    return result;
}

/**
 * Calculate discount for GLOBAL promotion type
 * Applies 2-for-1 across all eligible screenings (can combine tickets from different screenings)
 */
function calculateGlobalPromotionDiscount(
    items: Item[],
    promotion: TwoForOnePromotion
): TwoForOneDiscountResult {
    const result: TwoForOneDiscountResult = {
        discountAmount: 0,
        freeTickets: 0,
        itemDiscounts: new Map(),
        freeItemIndices: new Set(),
    };

    // Collect all eligible items (regardless of screening) for GLOBAL promotions
    const eligibleItems: Array<{ index: number; item: Item; price: number }> = [];

    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const isEligible = isEligibleForPromotion(item, promotion, i);

        // Skip if not eligible or missing screening ID
        if (!isEligible) continue;
        
        if (!item.screening?.id) {
            continue;
        }

        const price = getEffectivePrice(item.ticket, item.screening);
        eligibleItems.push({ index: i, item, price });
    }

    if (eligibleItems.length === 0) {
        return result;
    }

    // Calculate total uncovered quantity across ALL screenings
    const totalUncoveredQuantity = eligibleItems.reduce(
        (sum: number, e: { index: number; item: Item; price: number }) => {
            const passQuantityUsed = e.item.passQuantityUsed || 0;
            const uncoveredQuantity = e.item.quantity - passQuantityUsed;
            return sum + Math.max(0, uncoveredQuantity);
        },
        0,
    );

    // Calculate free tickets across all screenings (multiples logic: 2=1 free, 4=2 free, etc.)
    const freeCount = getFreeTicketCount(totalUncoveredQuantity);

    if (freeCount === 0) {
        return result;
    }

    // Sort all eligible items by price (cheapest first) to apply discount across screenings
    eligibleItems.sort(
        (
            a: { index: number; item: Item; price: number },
            b: { index: number; item: Item; price: number },
        ) => a.price - b.price,
    );

    let remainingFree = freeCount;

    // Apply 100% discount to cheapest tickets across all screenings
    for (const { index, item, price } of eligibleItems) {
        if (remainingFree <= 0) break;

        // Only apply discount to uncovered tickets (exclude those covered by passes)
        const passQuantityUsed = item.passQuantityUsed || 0;
        const uncoveredQuantity = item.quantity - passQuantityUsed;
        
        if (uncoveredQuantity <= 0) continue; // Skip if all tickets are covered by passes

        const discountQuantity = Math.min(uncoveredQuantity, remainingFree);
        const discount = price * discountQuantity;

        // Add to existing discount if item already has one
        const existingDiscount = result.itemDiscounts.get(index) || 0;
        result.itemDiscounts.set(index, existingDiscount + discount);
        result.freeItemIndices.add(index);
        result.discountAmount += discount;
        result.freeTickets += discountQuantity;
        remainingFree -= discountQuantity;
    }

    return result;
}

/**
 * Calculate discount for SELECTED_SCREENING promotion type
 */
function calculateSelectedScreeningDiscount(
    items: Item[],
    promotion: TwoForOnePromotion
): TwoForOneDiscountResult {
    const result: TwoForOneDiscountResult = {
        discountAmount: 0,
        freeTickets: 0,
        itemDiscounts: new Map(),
        freeItemIndices: new Set(),
    };

    if (!promotion.screening_ids || promotion.screening_ids.length === 0) {
        return result;
    }

    // Process each screening in the promotion
    for (const screeningId of promotion.screening_ids) {
        // Find all items for this screening
        const screeningItems: Array<{ index: number; item: Item; price: number }> = [];

        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            // Must match screening ID exactly
            if (item.screening?.id !== screeningId) continue;

            if (!isEligibleForPromotion(item, promotion, i)) continue;

            const price = getEffectivePrice(item.ticket, item.screening);
            screeningItems.push({ index: i, item, price });
        }

        // Calculate total quantity for this screening, excluding tickets covered by passes
        const totalQuantity = screeningItems.reduce((sum, e) => {
            const passQuantityUsed = e.item.passQuantityUsed || 0;
            const uncoveredQuantity = e.item.quantity - passQuantityUsed;
            return sum + Math.max(0, uncoveredQuantity);
        }, 0);

        // Rule: Must have 2+ tickets for the SAME screening
        if (totalQuantity < 2) continue;

        // Calculate free tickets
        const freeCount = getFreeTicketCount(totalQuantity);

        if (freeCount === 0) continue;

        // Sort by price (cheapest first)
        screeningItems.sort((a, b) => a.price - b.price);

        let remainingFree = freeCount;

        // Apply 100% discount to cheapest tickets
        for (const { index, item, price } of screeningItems) {
            if (remainingFree <= 0) break;

            // Only apply discount to uncovered tickets (exclude those covered by passes)
            const passQuantityUsed = item.passQuantityUsed || 0;
            const uncoveredQuantity = item.quantity - passQuantityUsed;
            
            if (uncoveredQuantity <= 0) continue; // Skip if all tickets are covered by passes

            const discountQuantity = Math.min(uncoveredQuantity, remainingFree);
            const discount = price * discountQuantity;

            // Add to existing discount if item already has one
            const existingDiscount = result.itemDiscounts.get(index) || 0;
            result.itemDiscounts.set(index, existingDiscount + discount);
            result.freeItemIndices.add(index);
            result.discountAmount += discount;
            remainingFree -= discountQuantity;
        }

        result.freeTickets += freeCount;
    }

    return result;
}

